import axios from '../../utils/axios';
import { API_URL } from '../../constants';
import { ItemType, LpuOrder, OrderType, PackLine } from './types';

// Mirrors server FulfillmentService.deriveOrderType: only B-prefix SKUs are bikes (go to QC).
// F (frame) and W (wheel) group with PA&A.
export const deriveOrderType = (itemTypes: ItemType[]): OrderType => {
  const hasBike = itemTypes.some(t => t === 'bike');
  const hasPaa = itemTypes.some(t => t === 'paa' || t === 'frame' || t === 'wheel');
  if (hasBike && hasPaa) return 'mixed';
  if (hasBike) return 'bike_only';
  if (hasPaa) return 'paa_only';
  return 'unknown';
};

interface LpuLineResponse {
  shopifyLineItemGid: string;
  sku: string | null;
  title: string | null;
  imageUrl: string | null;
  upc: string | null;
  mpn: string | null;
  itemType: PackLine['itemType'];
  binLocations: PackLine['binLocations'];
}

interface LpuOrderResponse {
  shopifyOrderId: number;
  shopifyOrderName: string;
  shopifyOrderGid: string;
  shopifyStoreName: string;
  createdAt: string;
  stagedAt: string | null;
  lifecycleStatus: 'picked' | 'lpu_staged';
  customer: { firstName: string; lastName: string; email: string };
  note: string;
  riskLevel: string;
  lines: LpuLineResponse[];
}

const toLpuOrder = (raw: LpuOrderResponse): LpuOrder => ({
  ...raw,
  lines: raw.lines.map(l => ({
    shopifyLineItemGid: l.shopifyLineItemGid,
    sku: l.sku ?? '',
    title: l.title ?? '',
    imageUrl: l.imageUrl,
    weightLbs: 0,
    upc: l.upc,
    mpn: l.mpn,
    itemType: l.itemType,
    binLocations: l.binLocations,
  })),
});

export async function getLpuQueue(): Promise<LpuOrder[]> {
  const res = await axios.get(`${API_URL}/fulfillment/lpu/queue`);
  const data = ((res as any)?.data ?? []) as LpuOrderResponse[];
  return data.map(toLpuOrder);
}

export async function getLpuStagedOrders(): Promise<LpuOrder[]> {
  const res = await axios.get(`${API_URL}/fulfillment/lpu/staged`);
  const data = ((res as any)?.data ?? []) as LpuOrderResponse[];
  return data.map(toLpuOrder);
}

export async function getLpuOrder(shopifyOrderId: number): Promise<LpuOrder | null> {
  try {
    const res = await axios.get(`${API_URL}/fulfillment/lpu/orders/${shopifyOrderId}`);
    return toLpuOrder((res as any).data as LpuOrderResponse);
  } catch {
    return null;
  }
}

export async function stageOrder(
  shopifyOrderId: number,
): Promise<{ success: boolean; error?: string }> {
  try {
    await axios.post(`${API_URL}/fulfillment/lpu/orders/${shopifyOrderId}/stage`);
    return { success: true };
  } catch (e) {
    return { success: false, error: e?.response?.data?.message ?? 'Failed to stage order' };
  }
}

export async function confirmPickup(
  shopifyOrderId: number,
): Promise<{ success: boolean; error?: string }> {
  try {
    await axios.post(`${API_URL}/fulfillment/lpu/orders/${shopifyOrderId}/confirm-pickup`);
    return { success: true };
  } catch (e) {
    return { success: false, error: e?.response?.data?.message ?? 'Failed to confirm pickup' };
  }
}
